import { hexToRgba } from "utils/data";
import { localizedNumber } from "shared/numberParser";
import { defaultHoverLabel, defaultTickFont } from "shared/plotly/defaultConfigs";
import { CONFIDENCE_RANGE, MainAxesName, } from "shared/plotly/types";
import { EngineResultType, } from "store/ps-workspace/ps-workspace.types";
import { renderNumberValue } from "utils/string";
import * as colors from "styles/colorValues.module.scss";
export const MAX_VISIBLE_AXES = 6;
export const GLOBAL_GRAPH_STYLES = {
    root: "pit-graph-root",
    header: "pit-graph-header",
    plotRoot: "pit-graph-plot-root",
    legendRoot: "pit-graph-legend-root",
    hiddenRow: "pit-graph-hidden-row",
    legendActions: "pit-graph-legend-actions",
};
const TEMP_LINE_COLORS = [
    "#7BC5D5",
    "#221874",
    "#2D6A6A",
    "#E9AD8C",
    "#DF6B87",
    "#5E38BA",
    "#F7EFA8",
    "#068B9D",
    "#A7CDA7",
    "#B43E3E",
    "#6E2970",
    "#F0D36D",
];
const TEMP_HEATMAP_COLORS = TEMP_LINE_COLORS.reduce((colors, item, i) => {
    const ch = Math.floor(i / 2);
    colors[ch] = [...(colors[ch] ?? []), item];
    return colors;
}, []);
export const getLineColor = (responseId, color, isLoading) => {
    const colorToGraph = color ? color : (TEMP_LINE_COLORS[responseId - 1] ?? "#fff");
    return isLoading ? hexToRgba(colorToGraph, 0.2) : colorToGraph;
};
export const getHeatMapColorScale = (responseId, minColor, maxColor, surfaceOpacity) => {
    const [minSurfaceColor, maxSurfaceColor] = TEMP_HEATMAP_COLORS[responseId - 1] ?? ["#fff", "#fff"];
    return [
        hexToRgba(minColor ?? minSurfaceColor[0], surfaceOpacity),
        hexToRgba(maxColor ?? maxSurfaceColor[1], surfaceOpacity),
    ];
};
export const sortByResponseId = (a, b) => {
    if ("responseId" in a && "responseId" in b) {
        return !a.responseId || !b.responseId ? 0 : a.responseId - b.responseId;
    }
    else if ("id" in a && "id" in b) {
        return !a.id || !b.id ? 0 : a.id - b.id;
    }
    else {
        return 0;
    }
};
const defaultAxisConfig = {
    tickfont: { ...defaultTickFont, color: colors.s600 },
    gridcolor: colors.s400,
    zerolinecolor: colors.black,
};
export const getWorkspaceAnalyses = (analyses) => analyses && analyses.map(({ name, id }) => ({ name, id }));
export const getConfigurationOptions = (analyses) => analyses &&
    analyses.reduce((acc, { id, configurations }) => ({
        ...acc,
        [id]: configurations.map(({ name, id }) => ({ name, id })) || [],
    }), {});
export const defaultPlotLayoutConfig = {
    autosize: true,
    legend: {
        x: 0,
        y: 0,
        xanchor: "left",
        yanchor: "bottom",
    },
    dragmode: "pan",
    hovermode: "x unified",
    hoverlabel: defaultHoverLabel,
};
const getAxisParameterName = (parameterId, dictionaryParameters) => {
    if (!dictionaryParameters || !Object.keys(dictionaryParameters).length) {
        return parameterId;
    }
    else {
        return parameterId ? (dictionaryParameters[parameterId]?.name ?? parameterId) : parameterId;
    }
};
const getAxisUnitName = (parameterId, unitsId, dictionaryParameters) => {
    if (!dictionaryParameters || !Object.keys(dictionaryParameters).length) {
        return unitsId;
    }
    else {
        return parameterId && unitsId
            ? (dictionaryParameters[parameterId]?.units.find(({ id }) => id === unitsId)?.name ?? unitsId)
            : unitsId;
    }
};
export const getAxisName = (parameterId, unitsId, dictionaryParameters) => {
    if (!dictionaryParameters || !Object.keys(dictionaryParameters).length) {
        return `${parameterId} ${unitsId ? `(${unitsId})` : ""}`;
    }
    else {
        const parameter = parameterId ? dictionaryParameters[parameterId]?.name : null;
        const unit = getAxisUnitName(parameterId, unitsId, dictionaryParameters);
        return `${parameter ?? "parameter"} ${unit ? `(${unit})` : ""}`;
    }
};
const getConfigurationName = (configurationOption, configurationId) => configurationOption?.find(({ id }) => id === configurationId)?.name ?? "";
export const replaceRanges = (axesRanges) => {
    axesRanges.reduce((a, b) => {
        if (a && b && a.min && a.max && b.min && b.max) {
            if (a.min >= b.min) {
                a.min = b.min;
            }
            else {
                b.min = a.min;
            }
            if (a.max <= b.max) {
                a.max = b.max;
            }
            else {
                b.max = a.max;
            }
        }
        return a;
    });
    return axesRanges;
};
export const prepareLegendDataItems = (items, dictionaryParameters, analysesOptions, configurationOptions) => {
    const legendData = items.map((payload, index) => {
        const { id = `wrongResponse-${index}`, xConfigurationId, yConfigurationId, analysisId } = payload;
        const analysis = analysesOptions.find(({ id }) => id === analysisId)?.name;
        const xConfig = analysisId
            ? getConfigurationName(configurationOptions[analysisId], xConfigurationId)
            : "";
        const yConfig = analysisId
            ? getConfigurationName(configurationOptions[analysisId], yConfigurationId)
            : "";
        if ("yParameterId" in payload) {
            const { yParameterId = "" } = payload;
            const yParameter = dictionaryParameters[yParameterId]?.name.toLowerCase() ?? "";
            return { id, analysis, xConfig, yConfig, yParameter };
        }
        else if ("zConfigurationId" in payload && "zParameterId" in payload) {
            const { zParameterId = "", zConfigurationId, zUnitsId } = payload;
            const zConfig = analysisId
                ? getConfigurationName(configurationOptions[analysisId], zConfigurationId)
                : "";
            const zParameter = dictionaryParameters[zParameterId]?.name.toLowerCase() ?? "";
            const zUnits = dictionaryParameters[zParameterId]?.units.find(({ id }) => id === zUnitsId)?.name;
            return { id, analysis, xConfig, yConfig, zConfig, zParameter, zUnits };
        }
        else {
            return { id, analysis, xConfig, yConfig };
        }
    });
    return legendData.reduce((dataItems, payload) => {
        const { id, analysis, ...dataToLegend } = payload;
        return {
            ...dataItems,
            ...(analysesOptions.length > 1 ? { [id]: { analysis, ...dataToLegend } } : { [id]: dataToLegend }),
        };
    }, {});
};
const prepareYAxesData = ({ responses }, dictionaryParameters, analiseOptions, configurationOptions) => {
    if (responses.length) {
        let legendDataItems = {};
        if (dictionaryParameters && analiseOptions && configurationOptions) {
            legendDataItems = prepareLegendDataItems(responses, dictionaryParameters, analiseOptions, configurationOptions);
        }
        return responses
            .slice()
            .sort(sortByResponseId)
            .reduce((obj, { id, visible, yParameterId, yUnitsId, color, yAutoscaling, minimumValue, maximumValue, locked, mergeId, }, index) => {
            const isConfidence = yParameterId === "CONFIDENCE";
            let range;
            if (isConfidence) {
                range = yAutoscaling ? [...CONFIDENCE_RANGE] : [minimumValue, maximumValue];
            }
            else {
                range = [minimumValue, maximumValue];
            }
            const autorange = isConfidence ? false : yAutoscaling;
            return {
                ...obj,
                [`${MainAxesName.Y_AXIS}${index ? index + 1 : ""}`]: {
                    ...defaultAxisConfig,
                    name: getAxisName(yParameterId, yUnitsId, dictionaryParameters),
                    unitName: getAxisUnitName(yUnitsId, yUnitsId, dictionaryParameters),
                    parameterName: getAxisParameterName(yUnitsId, dictionaryParameters),
                    color: getLineColor(id, color?.hexValue),
                    position: 2,
                    tickwidth: 1,
                    tickformat: "000",
                    tickangle: -45,
                    ...(!autorange && { range }),
                    isConfidence,
                    autorange: yAutoscaling,
                    ...(!!index && { overlaying: `y` }),
                    responseId: id,
                    fixedrange: locked || !visible,
                    legendData: id ? (legendDataItems[id] ?? {}) : {},
                    visible,
                    mergeId,
                    axisName: `${MainAxesName.Y_AXIS}${index ? index + 1 : ""}`,
                },
            };
        }, {});
    }
    else {
        return {
            yaxis: { ...defaultAxisConfig, position: 2, tickwidth: 1, tickformat: "000" },
        };
    }
};
const prepareZAxesData = ({ responses }, dictionaryParameters, analiseOptions, configurationOptions) => {
    let legendDataItems = {};
    if (dictionaryParameters && analiseOptions && configurationOptions) {
        legendDataItems = prepareLegendDataItems(responses, dictionaryParameters, analiseOptions, configurationOptions);
    }
    return responses
        .slice()
        .sort(sortByResponseId)
        .reduce((obj, { id, maxColor, visible, zParameterId, zUnitsId }, index) => ({
        ...obj,
        [`zaxis${index ? index + 1 : ""}`]: {
            name: getAxisName(zParameterId, zUnitsId, dictionaryParameters),
            responseId: id,
            color: getLineColor(id, maxColor?.hexValue),
            legendData: id ? (legendDataItems[id] ?? {}) : {},
            visible,
        },
    }), {});
};
const prepareXAxisData = ({ xAxis }, dictionaryParameters) => {
    const { parameterId, unitsId, minimumValue, maximumValue, autorange, granularity = "", locked, uiGranularity, mergeId, } = {
        ...xAxis,
    };
    return {
        ...defaultAxisConfig,
        spikedash: "5px",
        spikethickness: 1.1,
        spikecolor: colors.black,
        name: getAxisName(parameterId, unitsId, dictionaryParameters),
        parameterName: getAxisParameterName(parameterId, dictionaryParameters),
        unitName: getAxisUnitName(parameterId, unitsId, dictionaryParameters),
        tickangle: 0,
        autorange,
        ...(!autorange && { range: [minimumValue, maximumValue] }),
        granularity,
        fixedrange: locked,
        uiGranularity,
        mergeId,
        axisName: MainAxesName.X_AXIS,
    };
};
const prepareYAxisData = ({ yAxis: { parameterId, maximumValue, minimumValue, unitsId, autorange, granularity, locked, uiGranularity, }, responses, }, dictionaryParameters) => ({
    ...defaultAxisConfig,
    name: getAxisName(parameterId, unitsId, dictionaryParameters),
    parameterName: getAxisParameterName(parameterId, dictionaryParameters),
    unitName: getAxisUnitName(parameterId, unitsId, dictionaryParameters),
    color: colors.s600,
    position: 2,
    tickwidth: 1,
    tickformat: "000",
    tickangle: -45,
    autorange,
    ...(!autorange && { range: [minimumValue, maximumValue] }),
    granularity,
    fixedrange: locked,
    visible: true,
    uiGranularity,
    responseId: responses[0]?.id,
    axisName: MainAxesName.Y_AXIS,
});
const { decimal, group } = localizedNumber.numberSeparators;
export const preparePlotLayout = (graphElementLayout, type, xDictionaryParameters, yDictionaryParameters, analiseOptions, configurationOptions) => {
    const { responses } = graphElementLayout;
    const visibleResponses = responses.filter(({ visible, id, mergeId }) => visible && (!mergeId || Number(mergeId) === Number(id)));
    const leftMargin = type === EngineResultType.Result2D ? 45 : visibleResponses.length > 0 ? visibleResponses.length * 45 : 45;
    return {
        margin: {
            l: leftMargin,
            r: 0,
            b: 23,
            t: 0,
        },
        separators: `${decimal}${group}`,
        ...defaultPlotLayoutConfig,
        xaxis: prepareXAxisData(graphElementLayout, xDictionaryParameters),
        ...(type === EngineResultType.Result2D
            ? {
                yaxis: prepareYAxisData(graphElementLayout, yDictionaryParameters),
            }
            : prepareYAxesData(graphElementLayout, yDictionaryParameters, analiseOptions, configurationOptions)),
        ...(type === EngineResultType.Result2D &&
            prepareZAxesData(graphElementLayout, yDictionaryParameters, analiseOptions, configurationOptions)),
    };
};
export const getAxisRangeText = (axesRanges, key) => {
    const { min, max } = { ...axesRanges[key] };
    return `${renderNumberValue(min)} - ${renderNumberValue(max)}`;
};
export const getSubAxesByParameterName = (parameterName, layout) => parameterName &&
    layout &&
    Object.values(layout).filter((i) => i.parameterName === parameterName && !!i.mergeId && Number(i.mergeId) !== i.responseId);
export const getHeadAxisByParameterName = (parameterName, layout) => Object.values(layout).find((i) => i.parameterName === parameterName && !!i.mergeId);
export const getAxisById = (layout, responseId) => {
    if (!responseId) {
        return layout[MainAxesName.X_AXIS];
    }
    return Object.values(layout).find((item) => item?.responseId === responseId);
};
export const isVisibleAxis = (axis) => {
    return ((axis && !axis.responseId) ||
        (axis.visible === true && (!axis.mergeId || +axis.mergeId === axis.responseId)));
};
export const isHeadAxis = (responseId, layout) => {
    const axis = getAxisById(layout, responseId);
    return axis && axis.mergeId && axis.responseId === +axis.mergeId;
};
export const isMergedAxis = (responseId, preparedPlotLayout) => {
    const axis = getAxisById(preparedPlotLayout, responseId);
    if (axis && axis.mergeId) {
        const headAxis = getAxisById(preparedPlotLayout, Number(axis.mergeId));
        return headAxis && headAxis.mergeId === axis.mergeId;
    }
    return false;
};
export const getRanges = (layout, axesRanges, changeAxis, values) => {
    const parseValues = Object.values(layout);
    const ranges = [];
    for (const axis of parseValues) {
        if (axis.axisName) {
            const range = axis?.range
                ? axis?.range
                : [
                    axesRanges[axis.axisName]?.min,
                    axesRanges[axis.axisName]?.max,
                ];
            const obj = {
                min: Number(axis.axisName === changeAxis.axisName ? (values ? values.min : range[0]) : range[0]),
                max: Number(axis.axisName === changeAxis.axisName ? (values ? values.max : range[1]) : range[1]),
                mergeId: axis.mergeId,
                axisName: axis.axisName,
                autorange: axis.responseId !== changeAxis.responseId ? axis.autorange : false,
                responseId: Number(axis.responseId),
            };
            ranges.push(obj);
        }
    }
    const rangesToReplaceValues = ranges.filter((i) => !!i.mergeId && i.mergeId === changeAxis.mergeId);
    const replacedRanges = rangesToReplaceValues.length
        ? [
            ...ranges.filter((i) => !i.mergeId || i.mergeId !== changeAxis.mergeId),
            ...replaceRanges(rangesToReplaceValues),
        ]
        : ranges;
    const result = {};
    for (const i of replacedRanges) {
        result[i.axisName] = i;
    }
    return result;
};
